import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./InventoryTable.css";
import { ChecklistIcon } from "@xola/icons";
import { BodyScrollEndEvent, CellClickedEvent, ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { InventoryRow } from "types";
import { AdjustInventoryModal } from "./AdjustInventoryModal";
import { AdjustAction } from "./adjustInventoryTypes";
import { InventoryProductCell } from "./InventoryProductCell";
import { InventorySkuCell } from "./InventorySkuCell";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { merchandiseLedgerPath } from "@/components/Reports/MerchandiseLedger/merchandiseLedgerRoute";

interface LedgerActionCellProps extends ICellRendererParams<InventoryRow> {
    onOpenLedger: (catalogItemId: string) => void;
}

const LedgerActionCell = ({ data, onOpenLedger }: LedgerActionCellProps) => {
    const handleClick = useMemoizedFn(() => data?.catalogItemId && onOpenLedger(data.catalogItemId));
    if (!data?.catalogItemId) {
        return null;
    }

    return (
        <SecondaryButton size="small" aria-label="Ledger" className="!px-2" onClick={handleClick}>
            <ChecklistIcon />
        </SecondaryButton>
    );
};

interface InventoryTableProps {
    lots: InventoryRow[];
    isLoading: boolean;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    isFetchingNextPage?: boolean;
    // Per-row Ledger button; only from the standalone inventory page, not the Manage Inventory modal
    // (which shouldn't navigate away mid-edit).
    showLedgerAction?: boolean;
}

const baseColumnDefs: ColDef<InventoryRow>[] = [
    { field: "productName", headerName: "Product", cellRenderer: InventoryProductCell, flex: 3, minWidth: 280 },
    { field: "sku", headerName: "SKU", cellRenderer: InventorySkuCell, flex: 2, minWidth: 160 },
    { field: "unavailable", headerName: "Unavailable", flex: 1, minWidth: 120, cellClass: "underline cursor-pointer" },
    { field: "committed", headerName: "Committed", flex: 1, minWidth: 110 },
    { field: "available", headerName: "Available", flex: 1, minWidth: 100, cellClass: "underline cursor-pointer" },
    { field: "onHand", headerName: "On hand", flex: 1, minWidth: 100 },
];

const getRowClass = (params: { data?: InventoryRow }) => clsx(params.data?.isDeleted && "opacity-50") || undefined;

const defaultColDef: ColDef = {
    flex: 1,
    menuTabs: [],
    filter: false,
    sortable: true,
    resizable: false,
    suppressMovable: true,
    suppressAutoSize: true,
    suppressHeaderMenuButton: true,
    cellStyle: { display: "flex", alignItems: "center" },
};

export const InventoryTable = ({
    lots,
    isLoading,
    hasNextPage,
    onLoadMore,
    isFetchingNextPage,
    showLedgerAction = false,
}: InventoryTableProps) => {
    const history = useHistory();
    const isLoadingMoreRef = useRef(false);
    const [adjustment, setAdjustment] = useState<{ lot: InventoryRow; action: AdjustAction }>();

    const handleOpenLedger = useMemoizedFn((catalogItemId: string) => {
        history.push(merchandiseLedgerPath({ catalogItemId }));
    });

    const columnDefs = useMemo<ColDef<InventoryRow>[]>(() => {
        if (!showLedgerAction) {
            return baseColumnDefs;
        }

        const ledgerColumn: ColDef<InventoryRow> = {
            flex: 0,
            width: 90,
            colId: "ledger",
            minWidth: 90,
            sortable: false,
            headerName: "Action",
            suppressSizeToFit: true,
            cellRenderer: LedgerActionCell,
            cellRendererParams: { onOpenLedger: handleOpenLedger },
        };

        return [...baseColumnDefs, ledgerColumn];
    }, [showLedgerAction, handleOpenLedger]);

    useEffect(() => {
        if (!isFetchingNextPage) {
            isLoadingMoreRef.current = false;
        }
    }, [isFetchingNextPage]);

    const handleBodyScrollEnd = useMemoizedFn((event: BodyScrollEndEvent) => {
        if (!hasNextPage || !onLoadMore || isFetchingNextPage || isLoadingMoreRef.current) {
            return;
        }

        const lastVisible = event.api.getLastDisplayedRowIndex();
        const total = event.api.getDisplayedRowCount();
        if (lastVisible >= total - 1) {
            isLoadingMoreRef.current = true;
            onLoadMore();
        }
    });

    const handleCellClicked = useMemoizedFn((params: CellClickedEvent<InventoryRow>) => {
        if (!params.data) {
            return;
        }

        const colId = params.column.getColId();
        if (colId === "available") {
            setAdjustment({ lot: params.data, action: AdjustAction.ADD_INVENTORY });
        } else if (colId === "unavailable") {
            setAdjustment({ lot: params.data, action: AdjustAction.MOVE_TO_UNAVAILABLE });
        }
    });

    const handleAdjustmentClose = useMemoizedFn(() => setAdjustment(undefined));

    return (
        <>
            <div className="ag-theme-alpine inventory-table flex h-full flex-col px-10 pt-4">
                <AgGridReact<InventoryRow>
                    suppressCellFocus
                    suppressContextMenu
                    suppressScrollOnNewData
                    tooltipMouseTrack
                    theme="legacy"
                    rowData={lots}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowHeight={70}
                    rowBuffer={15}
                    suppressRowHoverHighlight={false}
                    animateRows={false}
                    suppressAnimationFrame={false}
                    domLayout="normal"
                    tooltipShowDelay={0}
                    loading={isLoading}
                    selectionColumnDef={{ pinned: "right", width: 48, suppressSizeToFit: true }}
                    getRowClass={getRowClass}
                    onBodyScrollEnd={handleBodyScrollEnd}
                    onCellClicked={handleCellClicked}
                />
                {isFetchingNextPage && (
                    <div className="text-gray-500 flex justify-center py-3 text-sm">Loading more...</div>
                )}
            </div>
            {adjustment && (
                <AdjustInventoryModal
                    isOpen
                    lot={adjustment.lot}
                    defaultAction={adjustment.action}
                    onClose={handleAdjustmentClose}
                />
            )}
        </>
    );
};
